import { useEffect } from 'react';
import {
  useSelectedOrganizationId,
  useAuthtoken,
  logout,
} from 'stores/useAuthStore';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';

import request from 'utils/request';
import { setAppLoading, showSnackbar } from 'stores/useUiStore';
import type { ApiRespBody } from 'types/api';
import type {
  ScheduledTask,
  ScheduledTaskTemplate,
  ExecutionHistory,
  ExecutionHistoryExecution,
} from './types';


const TOKEN_INVALID = 'Token is not valid';

export const SCHEDULED_TASKS_QUERY_KEY = ['scheduledTasks'] as const;
export const SCHEDULED_TASK_HISTORY_QUERY_KEY = [
  'scheduledTaskHistory',
] as const;

function authHeaders(authToken: string) {
  return {
    'Content-Type': 'application/json',
    authorization: authToken,
  };
}

async function fetchScheduledTasks(
  authToken: string
): Promise<ScheduledTask[]> {
  const result = await request<ApiRespBody<ScheduledTask[]>>(
    '/api/scheduledTasks',
    { method: 'GET', headers: authHeaders(authToken) }
  );
  if (!result?.success || !result.data) {
    if (result?.message === TOKEN_INVALID) throw new Error(TOKEN_INVALID);
    throw new Error(result?.message || 'Failed to load scheduled tasks');
  }
  return result.data;
}

async function fetchExecutionHistory(
  scheduleId: string,
  authToken: string
): Promise<ExecutionHistoryExecution[]> {
  const result = await request<ApiRespBody<ExecutionHistory>>(
    `/api/scheduledTasks/${scheduleId}/executionHistory`,
    { method: 'GET', headers: authHeaders(authToken) }
  );
  if (!result?.success || !result.data) {
    if (result?.message === TOKEN_INVALID) throw new Error(TOKEN_INVALID);
    throw new Error(result?.message || 'Failed to load execution history');
  }
  return result.data.executions ?? [];
}

export function scheduledTasksQueryOptions(
  authToken: string,
  organizationId: number
) {
  return queryOptions({
    queryKey: [...SCHEDULED_TASKS_QUERY_KEY, organizationId],
    queryFn: () => fetchScheduledTasks(authToken),
  });
}

export function executionHistoryQueryOptions(
  scheduleId: string | null | undefined,
  authToken: string
) {
  return queryOptions({
    queryKey: [...SCHEDULED_TASK_HISTORY_QUERY_KEY, scheduleId],
    queryFn: () => {
      if (!scheduleId) {
        throw new Error('Schedule ID is required');
      }
      return fetchExecutionHistory(scheduleId, authToken);
    },
    enabled: !!scheduleId,
  });
}

function useScheduledTaskQueryError(error: unknown, caller: string) {
  useEffect(() => {
    if (!(error instanceof Error)) return;
    if (error.message === TOKEN_INVALID) {
      logout();
      return;
    }
    showSnackbar({
      autoHideDuration: null,
      caller,
      open: true,
      message: error.message,
      severity: 'error',
    });
  }, [caller, error]);
}

export function useScheduledTasksQuery() {
  const authToken = useAuthtoken();
  const organizationId = useSelectedOrganizationId();
  const query = useQuery(
    scheduledTasksQueryOptions(authToken, organizationId)
  );
  useScheduledTaskQueryError(query.error, 'scheduledTasks.query');
  return query;
}

export function useExecutionHistoryQuery(scheduleId: string | null) {
  const authToken = useAuthtoken();
  const query = useQuery(executionHistoryQueryOptions(scheduleId, authToken));
  useScheduledTaskQueryError(query.error, 'scheduledTaskHistory.query');
  return query;
}

export function useScheduledTaskMutations() {
  const authToken = useAuthtoken();
  const queryClient = useQueryClient();
  const headers = authHeaders(authToken);

  function mutationOptions<TVars>(cfg: {
    label: string;
    caller: string;
    successMessage: string | ((vars: TVars) => string);
    run: (vars: TVars) => Promise<ApiRespBody | null>;
    invalidateList?: boolean;
    onDone?: (vars: TVars) => void;
  }): UseMutationOptions<void, Error, TVars> {
    return {
      mutationFn: async (vars: TVars) => {
        setAppLoading({ isLoading: true, label: cfg.label });
        const result = await cfg.run(vars);
        if (!result?.success) {
          throw new Error(result?.message || `${cfg.caller} failed`);
        }
      },
      onSuccess: (_data, vars) => {
        showSnackbar({
            autoHideDuration: 5000,
            caller: cfg.caller,
            open: true,
            message:
              typeof cfg.successMessage === 'function'
                ? cfg.successMessage(vars)
                : cfg.successMessage,
            severity: 'success',
          });
        if (cfg.invalidateList !== false) {
          queryClient.invalidateQueries({
            queryKey: SCHEDULED_TASKS_QUERY_KEY,
          });
        }
        cfg.onDone?.(vars);
      },
      onError: (error) => {
        if (error.message === TOKEN_INVALID) {
          logout();
          return;
        }
        showSnackbar({
            autoHideDuration: null,
            caller: cfg.caller,
            open: true,
            message: error.message,
            severity: 'error',
          });
      },
      onSettled: () => {
        setAppLoading({ isLoading: false, label: '' });
      },
    };
  }

  const addMutation = useMutation(
    mutationOptions<ScheduledTask | ScheduledTaskTemplate>({
      label: 'Adding Scheduled Task',
      caller: 'onScheduledTaskAdd',
      successMessage: 'Scheduled Task Added',
      run: (task) =>
        request<ApiRespBody>('/api/scheduledTasks', {
          method: 'POST',
          body: JSON.stringify(task),
          headers,
        }),
    })
  );

  const updateMutation = useMutation(
    mutationOptions<ScheduledTask>({
      label: 'Updating Scheduled Task',
      caller: 'onScheduledTaskUpdate',
      successMessage: 'Scheduled Task Updated',
      run: (task) =>
        request<ApiRespBody>(`/api/scheduledTasks/${task.scheduleId}`, {
          method: 'PUT',
          body: JSON.stringify(task),
          headers,
        }),
    })
  );

  const deleteMutation = useMutation(
    mutationOptions<ScheduledTask>({
      label: 'Deleting Scheduled Task',
      caller: 'onScheduledTaskDelete',
      successMessage: 'Scheduled Task Deleted',
      run: (task) =>
        request<ApiRespBody>(`/api/scheduledTasks/${task.scheduleId}`, {
          method: 'DELETE',
          headers,
        }),
    })
  );

  const toggleMutation = useMutation(
    mutationOptions<ScheduledTask>({
      label: 'Toggling Scheduled Task',
      caller: 'onScheduledTaskToggle',
      successMessage: (task) =>
        `Scheduled Task successfully ${task.paused ? 'Paused' : 'Scheduled'}`,
      run: (task) =>
        request<ApiRespBody>(`/api/scheduledTasks/${task.scheduleId}/toggle`, {
          method: 'PATCH',
          body: JSON.stringify({ paused: task.paused }),
          headers,
        }),
    })
  );

  const triggerMutation = useMutation(
    mutationOptions<ScheduledTask>({
      label: 'Triggering Workflow',
      caller: 'onScheduledTaskTrigger',
      successMessage: 'Workflow triggered successfully',
      run: (task) =>
        request<ApiRespBody>(
          `/api/scheduledTasks/${task.scheduleId}/trigger`,
          { method: 'POST', headers }
        ),
    })
  );

  const cancelExecutionMutation = useMutation(
    mutationOptions<{
      execution: ExecutionHistoryExecution;
      scheduledTask: ScheduledTask;
    }>({
      label: 'Terminating Execution',
      caller: 'onScheduledTaskExecutionCancel',
      successMessage: 'Execution terminated successfully',
      invalidateList: false,
      run: ({ execution }) =>
        request<ApiRespBody>('/api/scheduledTasks/cancelExecution', {
          method: 'POST',
          body: JSON.stringify({
            workflowId: execution.workflowId,
            runId: execution.executionId,
          }),
          headers,
        }),
      // Allow Temporal a moment to update the workflow status, then refetch the
      onDone: ({ scheduledTask }) => {
        setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: [
              ...SCHEDULED_TASK_HISTORY_QUERY_KEY,
              scheduledTask.scheduleId,
            ],
          });
        }, 1000);
      },
    })
  );

  return {
    addScheduledTask: (task: ScheduledTask | ScheduledTaskTemplate) =>
      addMutation.mutate(task),
    updateScheduledTask: (task: ScheduledTask) => updateMutation.mutate(task),
    deleteScheduledTask: (task: ScheduledTask) => deleteMutation.mutate(task),
    toggleScheduledTask: (task: ScheduledTask) => toggleMutation.mutate(task),
    triggerScheduledTask: (task: ScheduledTask) => triggerMutation.mutate(task),
    cancelExecution: (
      execution: ExecutionHistoryExecution,
      scheduledTask: ScheduledTask
    ) => cancelExecutionMutation.mutate({ execution, scheduledTask }),
  };
}
